<script setup lang="ts">
/**
 * CodeMirror 6 を使った、NGワードのマーキング(下線+ホバー説明)を
 * 入力エリアそのものに表示する編集コンポーネント。
 *
 * 【なぜ自前のcontenteditable実装(旧NgEditableArea)をやめたか】
 * 自前実装では「カーソル位置」をDOMのノード/オフセットのペアで扱っていた。
 * しかしHTML側の改行表現(<br>か\nか)、ブラウザのinnerText正規化、
 * 空行の扱いなど、DOM構造と文字列表現の対応関係はブラウザ依存の細部が多く、
 * 直しても直しても新しい食い違いのパターンが出てきた(実際に3回直して
 * 3回とも別の症状が再発した)。
 *
 * CodeMirrorは逆に「文書は常にプレーンテキストの1本の文字列」として内部管理し、
 * カーソル/選択は「先頭からの文字数(整数)」という1種類の座標系だけで表現する。
 * 見た目の下線(Decoration.mark)は文書そのものを書き換えずに「範囲を装飾する」
 * レイヤーとして重ねるだけなので、マーキングを更新してもカーソル位置は
 * 一切ずれない(自前実装のように「再描画してカーソルを復元する」という
 * 壊れやすい処理が丸ごと不要になる)。
 * IME(日本語入力)のcomposition処理もエディタ本体が内部で正しく扱う。
 */
import { onMounted, onBeforeUnmount, watch, ref } from 'vue'
import { EditorState, StateEffect, StateField, type Extension } from '@codemirror/state'
import { EditorView, Decoration, type DecorationSet, keymap } from '@codemirror/view'
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands'
import { buildNgSegments } from '@/utils/ngHighlight'
import { useNgMarkStore } from '@/stores/ngMark'
import type { NgWordMatch } from '@/types/api'

/**
 * 改行コードを \n に統一する。
 *
 * 【なぜ必要か】DBに保存されている元データや、コピー&ペーストされた文章には
 * \r\n(CRLF)が混ざっていることがある。ところがCodeMirrorは内部で文書を
 * 保持する際、\r\n を自動的に \n(1文字)に正規化してしまう
 * (例: "1行目\r\n2行目" → doc上は "1行目\n2行目" になり、2文字短くなる)。
 *
 * 一方、NGワードの位置(matches)はサーバー側がDBの生データ(\r\n込み)を
 * 元に計算して返す。エディタが保持する文字列と、位置計算の元になった
 * 文字列の「改行の数え方」が食い違うと、行を追うごとに位置がずれていく
 * (改行1つにつき1文字ぶんズレが蓄積する)。
 *
 * これを防ぐため、CodeMirrorに渡す前・NGワードチェックAPIに送る前の
 * 両方で \r\n → \n に統一し、「エディタが見ている文字列」と
 * 「サーバーが位置を計算した文字列」を完全に一致させる。
 */
function normalizeNewlines(text: string): string {
  return text.replace(/\r\n/g, '\n')
}

const props = defineProps<{
  modelValue: string
  ngWords: NgWordMatch[]
  field: string
  rows?: number
}>()
const emit = defineEmits<{ 'update:modelValue': [string] }>()

const ngMark = useNgMarkStore()
const hostEl = ref<HTMLDivElement | null>(null)
let view: EditorView | null = null

// ---- NGワードのマーキングをCodeMirrorの装飾(Decoration)として表現する ----
// StateEffectでプラグイン外部から装飾セットを差し替えられるようにし、
// StateFieldでその装飾セットを保持する。これはCodeMirrorの標準的な
// 「外部データに基づく装飾」の実装パターン。
const setMarksEffect = StateEffect.define<DecorationSet>()
const marksField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(marks, tr) {
    // 文書が編集されたら装飾の位置を追従させ(mapPos相当)、
    // 明示的な差し替え指示(setMarksEffect)が来たらそちらを優先する
    marks = marks.map(tr.changes)
    for (const e of tr.effects) if (e.is(setMarksEffect)) marks = e.value
    return marks
  },
  provide: (f) => EditorView.decorations.from(f),
})

/** buildNgSegments の結果から Decoration.mark の集合を組み立てる */
function buildDecorations(text: string): DecorationSet {
  const segments = buildNgSegments(text, props.ngWords, ngMark.activeLevels, props.field)
  const marks = []
  let pos = 0
  for (const seg of segments) {
    if (seg.hits.length > 0) {
      const lv = seg.maxLevel >= 9 ? 9 : seg.maxLevel >= 7 ? 7 : 5
      const words = seg.hits.map((h) => h.word).join('\u001F')
      marks.push(
        Decoration.mark({
          class: `ng-mark ng-mark-lv${lv}`,
          attributes: { 'data-ng-words': words },
        }).range(pos, pos + seg.text.length),
      )
    }
    pos += seg.text.length
  }
  return Decoration.set(marks)
}

function applyDecorations() {
  if (!view) return
  view.dispatch({ effects: setMarksEffect.of(buildDecorations(view.state.doc.toString())) })
}

// ---- ホバーで詳細説明(既存のNgHitDetailPaneと同じ仕組みを使う) ----
function onMarkEnter(e: MouseEvent) {
  const target = e.target as HTMLElement
  const wrapEl = target.closest<HTMLElement>('.ng-mark')
  if (!wrapEl) return
  const words = (wrapEl.dataset.ngWords ?? '').split('\u001F')
  const hits = props.ngWords.filter((w) => words.includes(w.word))
  if (hits.length === 0) return
  const r = wrapEl.getBoundingClientRect()
  ngMark.setHovered(hits, { left: r.left, right: r.right, top: r.top, bottom: r.bottom })
}
function onMarkLeave(e: MouseEvent) {
  const target = e.target as HTMLElement
  if (target.closest?.('.ng-mark')) ngMark.scheduleClose()
}

onMounted(() => {
  if (!hostEl.value) return

  const updateListener = EditorView.updateListener.of((update) => {
    if (update.docChanged) emit('update:modelValue', update.state.doc.toString())
  })

  const extensions: Extension[] = [
    history(),
    keymap.of([...defaultKeymap, ...historyKeymap]),
    marksField,
    updateListener,
    EditorView.lineWrapping,
    EditorView.theme({
      // 実際の高さは外側のホスト要素(テンプレートのインラインstyle)で決める。
      // .cm-editor はその中を100%で満たすだけにする(二重に高さを持たせない)。
      '&': {
        fontSize: 'var(--edit-font-size)',
        height: '100%',
      },
      '.cm-content': {
        fontFamily: 'var(--edit-font-family)',
        lineHeight: 'var(--edit-line-height)',
        padding: 'var(--edit-padding)',
        caretColor: 'var(--ink)',
      },
      '.cm-line': { padding: '0' },
      '&.cm-focused': { outline: 'none' },
      '.cm-scroller': { overflow: 'auto' },
    }),
  ]

  view = new EditorView({
    state: EditorState.create({ doc: normalizeNewlines(props.modelValue), extensions }),
    parent: hostEl.value,
  })

  hostEl.value.addEventListener('mouseover', onMarkEnter)
  hostEl.value.addEventListener('mouseout', onMarkLeave)

  applyDecorations()
})

onBeforeUnmount(() => {
  hostEl.value?.removeEventListener('mouseover', onMarkEnter)
  hostEl.value?.removeEventListener('mouseout', onMarkLeave)
  view?.destroy()
  view = null
})

// NGワードの判定結果、レベルフィルタが変わったら装飾を作り直す。
// 文書そのものは書き換えないため、カーソル位置は一切影響を受けない
// (これが自前contenteditable実装との決定的な違い)。
watch(() => props.ngWords, applyDecorations)
watch(() => ngMark.activeLevels, applyDecorations, { deep: true })

// 親側でmodelValueが外部から差し替えられた場合(初期値のセットなど)に追従する。
// ただしユーザーの入力によるemitでmodelValueが変わった直後は、
// view側は既に最新なので二重反映しないようガードする。
watch(
  () => props.modelValue,
  (v) => {
    if (!view) return
    const normalized = normalizeNewlines(v)
    if (view.state.doc.toString() === normalized) return
    view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: normalized } })
  },
)
</script>

<template>
  <!-- .edit-textarea の既定height(340px)は特徴・商品説明向けの固定値。
       商品名のように行数を絞りたい場合だけ rows を渡すと、ここでインライン
       styleとして高さを上書きする(インラインstyleはCSSクラスより優先される)。
       rowsを渡さない項目(特徴・商品説明)は既定の340pxのまま使われる。 -->
  <div
    ref="hostEl"
    class="edit-textarea edit-codemirror"
    :style="rows ? { height: `calc(${rows} * 1.8em + 18px)` } : undefined"
  ></div>
</template>
